/**
 * Task #3038 — Routine personali del Personal Assistant.
 *
 * Contratti condivisi BE↔FE (Zod + z.infer, mai `any`):
 *  - spec ad alto livello dei passi (linguaggio controllato che il PA compila
 *    verso il WorkflowEngine — l'utente non vede MAI questa struttura);
 *  - DTO risposta delle rotte `/api/pa-routines` (routine + run);
 *  - body schemas per le azioni di gestione (schedule, conferma conteggio).
 *
 * Regola cardine: la routine è DELLA PERSONA (tenant_id + user_id) ed esegue
 * sempre impersonando il proprietario. Nessuna terminologia workflow esposta.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Catalogo inneschi v1 — ristretto e fisso. Solo eventi con fire point
// verificato live (Meta Messenger/Instagram esclusi per decisione esplicita;
// appuntamenti esclusi: nessun fire point live oggi).
// ---------------------------------------------------------------------------
export const PA_ROUTINE_EVENT_TYPES = [
  'lead_created',
  'whatsapp_received',
  'telegram_received',
] as const;

export const PaRoutineEventTypeSchema = z.enum(PA_ROUTINE_EVENT_TYPES);
export type PaRoutineEventType = z.infer<typeof PaRoutineEventTypeSchema>;

export const PA_ROUTINE_EVENT_LABELS: Record<PaRoutineEventType, string> = {
  lead_created: 'Nuovo lead',
  whatsapp_received: 'Nuovo messaggio WhatsApp',
  telegram_received: 'Nuovo messaggio Telegram',
};

export const PaRoutineTriggerKindSchema = z.enum(['time', 'event']);
export type PaRoutineTriggerKind = z.infer<typeof PaRoutineTriggerKindSchema>;

// ---------------------------------------------------------------------------
// Spec dei passi (compilata dal PA, mai mostrata all'utente).
// Preferenza deterministica: `tool` (chiamata fissa) > `ai_generate`
// (testo in → testo out, nessun tool) > `ai_agent` (agentico-limitato,
// whitelist tool + round bassi — eccezione, "meno prevedibile").
// ---------------------------------------------------------------------------
const SAVE_AS_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

export const PaRoutineToolStepSchema = z.object({
  kind: z.literal('tool'),
  /** Etichetta umana del passo (compare nel registro esecuzioni). */
  label: z.string().min(1).max(200),
  /** Nome canonico del tool (catalogo MCP/interno esistente). */
  tool: z.string().min(1).max(120),
  /** Argomenti fissi; i valori stringa supportano placeholder {{variabile}}. */
  args: z.record(z.unknown()).optional(),
  /** Nome variabile in cui salvare l'output (per i passi successivi). */
  save_as: z.string().regex(SAVE_AS_RE).max(60).optional(),
});

export const PaRoutineAiGenerateStepSchema = z.object({
  kind: z.literal('ai_generate'),
  label: z.string().min(1).max(200),
  /** Prompt con placeholder {{variabile}}; NESSUN tool: solo generazione. */
  prompt: z.string().min(1).max(8000),
  system_prompt: z.string().max(4000).optional(),
  output_format: z.enum(['text', 'json']).optional(),
  save_as: z.string().regex(SAVE_AS_RE).max(60).optional(),
});

export const PaRoutineAiAgentStepSchema = z.object({
  kind: z.literal('ai_agent'),
  label: z.string().min(1).max(200),
  prompt: z.string().min(1).max(8000),
  system_prompt: z.string().max(4000).optional(),
  /** Whitelist ristretta di tool consentiti (agentico-limitato). */
  enabled_tools: z.array(z.string().min(1)).min(1).max(10),
  max_tool_rounds: z.number().int().min(1).max(5).optional(),
  save_as: z.string().regex(SAVE_AS_RE).max(60).optional(),
});

const PaRoutineLeafStepSchema = z.discriminatedUnion('kind', [
  PaRoutineToolStepSchema,
  PaRoutineAiGenerateStepSchema,
  PaRoutineAiAgentStepSchema,
]);
export type PaRoutineLeafStep = z.infer<typeof PaRoutineLeafStepSchema>;

export const PaRoutineForeachStepSchema = z.object({
  kind: z.literal('foreach'),
  label: z.string().min(1).max(200),
  /** Path della variabile array su cui iterare (es. `risultati.items`). */
  array_path: z.string().min(1).max(200),
  /** Nome variabile dell'elemento corrente dentro il corpo. */
  item_variable: z.string().regex(SAVE_AS_RE).max(60).optional(),
  /** Passi eseguiti per ogni elemento (niente foreach annidati). */
  steps: z.array(PaRoutineLeafStepSchema).min(1).max(6),
});
export type PaRoutineForeachStep = z.infer<typeof PaRoutineForeachStepSchema>;

export const PaRoutineStepSchema = z.union([
  PaRoutineLeafStepSchema,
  PaRoutineForeachStepSchema,
]);
export type PaRoutineStep = z.infer<typeof PaRoutineStepSchema>;

export const PaRoutineTriggerSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('time'),
    cron_expression: z.string().min(1).max(128),
    // Optional (default applicato lato service): niente `.default()` qui perché
    // i tool MCP richiedono schema con input===output (ZodType<TArgs>).
    timezone: z.string().min(1).max(64).optional(),
  }),
  z.object({
    kind: z.literal('event'),
    event_type: PaRoutineEventTypeSchema,
  }),
]);
export type PaRoutineTrigger = z.infer<typeof PaRoutineTriggerSchema>;

/** Spec completa della routine (persistita in `pa_routines.spec`). */
export const PaRoutineSpecSchema = z.object({
  steps: z.array(PaRoutineStepSchema).min(1).max(15),
});
export type PaRoutineSpec = z.infer<typeof PaRoutineSpecSchema>;

// ---------------------------------------------------------------------------
// DTO risposta — rotte /api/pa-routines.
// ---------------------------------------------------------------------------
export const PaRoutineRunStatusSchema = z.enum([
  'running',
  'awaiting_confirmation',
  'completed',
  'failed',
  'cancelled',
]);
export type PaRoutineRunStatus = z.infer<typeof PaRoutineRunStatusSchema>;

export const PaRoutineSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  trigger_kind: PaRoutineTriggerKindSchema,
  cron_expression: z.string().nullable(),
  timezone: z.string(),
  event_type: PaRoutineEventTypeSchema.nullable(),
  is_active: z.boolean(),
  is_dangerous: z.boolean(),
  proven_at: z.string().nullable(),
  max_items_per_run: z.number().int(),
  ai_provider: z.string().nullable(),
  ai_model: z.string().nullable(),
  next_run_at: z.string().nullable(),
  last_run_at: z.string().nullable(),
  last_run_id: z.string().uuid().nullable(),
  last_run_status: PaRoutineRunStatusSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type PaRoutine = z.infer<typeof PaRoutineSchema>;

/** Voce del registro esecuzioni: un passo eseguito, in forma leggibile. */
export const PaRoutineRunStepSchema = z.object({
  nodeId: z.string(),
  nodeName: z.string(),
  status: z.string(),
  startedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  durationMs: z.number().optional(),
  error: z.string().optional(),
  output: z.unknown().optional(),
});
export type PaRoutineRunStep = z.infer<typeof PaRoutineRunStepSchema>;

export const PaRoutinePendingConfirmationSchema = z.object({
  step_label: z.string(),
  count: z.number().int(),
  message: z.string(),
});
export type PaRoutinePendingConfirmation = z.infer<typeof PaRoutinePendingConfirmationSchema>;

export const PaRoutineRunSchema = z.object({
  id: z.string().uuid(),
  routine_id: z.string().uuid(),
  status: PaRoutineRunStatusSchema,
  trigger_source: z.enum(['manual', 'schedule', 'event']),
  started_at: z.string(),
  completed_at: z.string().nullable(),
  error_message: z.string().nullable(),
  pending_confirmation: PaRoutinePendingConfirmationSchema.nullable(),
  confirmation_status: z.enum(['pending', 'approved', 'rejected']).nullable(),
});
export type PaRoutineRun = z.infer<typeof PaRoutineRunSchema>;

export const PaRoutineRunDetailSchema = PaRoutineRunSchema.extend({
  node_logs: z.array(PaRoutineRunStepSchema),
});
export type PaRoutineRunDetail = z.infer<typeof PaRoutineRunDetailSchema>;

export const PaRoutineListDataSchema = z.array(PaRoutineSchema);
export type PaRoutineListData = z.infer<typeof PaRoutineListDataSchema>;

export const PaRoutineRunListDataSchema = z.object({
  items: z.array(PaRoutineRunSchema),
  pagination: z.object({
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
  }),
});
export type PaRoutineRunListData = z.infer<typeof PaRoutineRunListDataSchema>;

export const PaRoutineRunStartDataSchema = z.object({
  run_id: z.string().uuid(),
});
export type PaRoutineRunStartData = z.infer<typeof PaRoutineRunStartDataSchema>;

export const PaRoutineRunDecisionDataSchema = z.object({
  decision: z.enum(['approve', 'reject']),
});
export type PaRoutineRunDecisionData = z.infer<typeof PaRoutineRunDecisionDataSchema>;

export const PaRoutineDeleteDataSchema = z.object({
  deleted: z.literal(true),
});
export type PaRoutineDeleteData = z.infer<typeof PaRoutineDeleteDataSchema>;

export const PaRoutineListResponseSchema = z.object({ data: PaRoutineListDataSchema });
export const PaRoutineResponseSchema = z.object({ data: PaRoutineSchema });
export const PaRoutineRunListResponseSchema = z.object({ data: PaRoutineRunListDataSchema });
export const PaRoutineRunDetailResponseSchema = z.object({ data: PaRoutineRunDetailSchema });
export const PaRoutineRunStartResponseSchema = z.object({ data: PaRoutineRunStartDataSchema });
export const PaRoutineRunDecisionResponseSchema = z.object({ data: PaRoutineRunDecisionDataSchema });
export const PaRoutineDeleteResponseSchema = z.object({ data: PaRoutineDeleteDataSchema });

// ---------------------------------------------------------------------------
// Body/query schemas — azioni di gestione.
// ---------------------------------------------------------------------------
export const PaRoutineScheduleUpdateBodySchema = z
  .object({
    trigger: PaRoutineTriggerSchema,
  })
  .strict();
export type PaRoutineScheduleUpdateBody = z.infer<typeof PaRoutineScheduleUpdateBodySchema>;

export const PaRoutineRunConfirmBodySchema = z
  .object({
    decision: z.enum(['approve', 'reject']),
  })
  .strict();
export type PaRoutineRunConfirmBody = z.infer<typeof PaRoutineRunConfirmBodySchema>;

export const PaRoutineRunListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(30),
  offset: z.coerce.number().int().min(0).default(0),
  status: PaRoutineRunStatusSchema.optional(),
});
export type PaRoutineRunListQuery = z.infer<typeof PaRoutineRunListQuerySchema>;

/** Tetto duro di elementi per esecuzione per le azioni di massa. */
export const PA_ROUTINE_MAX_ITEMS_HARD_CAP = 200;
export const PA_ROUTINE_DEFAULT_MAX_ITEMS = 25;
